import { useState } from 'react';
import { sendContactFormNotification } from '../../modules/email/emailService';
import type { ContactFormData } from '../../types';
import { validateContactForm, hasErrors } from '../../lib/validation';
import { Button } from '../../components/ui/Button';
import styles from './ContactPage.module.css';

const EMPTY: ContactFormData = { name: '', email: '', phone: '', message: '' };

export function ContactPage() {
  const [form, setForm] = useState<ContactFormData>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateContactForm(form);
    if (hasErrors(errs)) { setErrors(errs); return; }
    setSubmitting(true);
    await sendContactFormNotification(form);
    setSubmitting(false);
    setSent(true);
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className="container">
          <h1 className={styles.title}>Contact Us</h1>
        </div>
      </div>

      <div className="container">
        <div className={styles.layout}>
          {sent ? (
            <div className={styles.confirmation}>
              <h2 className={styles.confirmTitle}>Message Sent</h2>
              <p>Thank you for reaching out. We'll get back to you shortly.</p>
              <button className={styles.again} onClick={() => { setForm(EMPTY); setSent(false); }}>
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="c-name" className={styles.label}>Name <span className={styles.req}>*</span></label>
                <input id="c-name" name="name" type="text" value={form.name} onChange={handleChange}
                  className={[styles.input, errors.name ? styles.inputError : ''].join(' ')} />
                {errors.name && <p className={styles.error}>{errors.name}</p>}
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label htmlFor="c-email" className={styles.label}>Email <span className={styles.req}>*</span></label>
                  <input id="c-email" name="email" type="email" value={form.email} onChange={handleChange}
                    className={[styles.input, errors.email ? styles.inputError : ''].join(' ')} />
                  {errors.email && <p className={styles.error}>{errors.email}</p>}
                </div>
                <div className={styles.field}>
                  <label htmlFor="c-phone" className={styles.label}>Phone</label>
                  <input id="c-phone" name="phone" type="tel" value={form.phone} onChange={handleChange}
                    className={styles.input} />
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="c-message" className={styles.label}>Message <span className={styles.req}>*</span></label>
                <textarea id="c-message" name="message" rows={5} value={form.message} onChange={handleChange}
                  className={[styles.textarea, errors.message ? styles.inputError : ''].join(' ')} />
                {errors.message && <p className={styles.error}>{errors.message}</p>}
              </div>

              <Button type="submit" size="lg" loading={submitting}>Send Message</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
